import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Send, Mail, Building, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = ({ onNavigateBack, initialPlan }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // When the visitor arrives from a pricing "Get Started" CTA, pre-fill the
  // message with a plan-tagged subject so sales can prioritise the lead.
  useEffect(() => {
    if (initialPlan) {
      setFormData((prev) => ({
        ...prev,
        message: prev.message
          ? prev.message
          : `Plan enquiry: ${initialPlan}\n\nHi PriceValuator team, I'd like to learn more about the ${initialPlan} plan. `,
      }));
    }
  }, [initialPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        message: formData.message
      });
      
      setSuccess(true);
      setLoading(false);
      setFormData({ name: '', company: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      setError('Failed to send message. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white page-transition">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
                alt="PriceValuator Logo" 
                className="h-48 object-contain"
              />
              <Button 
                variant="ghost" 
                onClick={onNavigateBack}
                className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          {initialPlan && (
            <div
              data-testid="contact-plan-pill"
              className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-4"
            >
              <Mail className="w-3.5 h-3.5" />
              Plan enquiry: {initialPlan}
            </div>
          )}
          <h1 className="text-4xl font-bold text-[#1E3A5F] mb-4">Contact Us</h1>
          <p className="text-lg text-gray-600">
            {initialPlan
              ? `Tell us a bit about your team and we'll walk you through the ${initialPlan} plan.`
              : "Have questions or need assistance? We're here to help!"}
          </p>
        </div>

        <Card className="rounded-2xl border-2 border-gray-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-2xl">
            <CardTitle className="text-2xl text-[#1E3A5F] flex items-center">
              <Mail className="w-6 h-6 mr-3 text-cyan-600" />
              Send us a message
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {success && (
              <Alert className="mb-6 bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">
                  Thank you for contacting us! We'll get back to you soon.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your full name"
                  className="w-full"
                  required
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  Company <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Enter your company name"
                  className="w-full"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="w-full"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help you?"
                  rows="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-8 py-3 font-semibold"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-12 text-center text-gray-600">
          <p className="mb-2">You can also reach us at:</p>
          <p className="font-medium text-[#1E3A5F]">admin@pricevaluator.ca</p>

          <div className="mt-8">
            <Button
              onClick={onNavigateBack}
              data-testid="contact-back-to-home-btn"
              variant="outline"
              className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full px-6 py-2 font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
